// components/RtkqPatchToast.tsx
import { Notification, Stack } from "@mantine/core";
import { IconCheck, IconExclamationMark, IconX } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import { RTKQ_PATCH_ERROR_EVENT, RTKQ_PATCH_EVENT } from "../app/patchSpyMiddleware";
import { RtkqPatchDetail, RtkqPatchErrorDetail } from "../rtkq-events";

type Variant = "success" | "warning" | "error";

const AUTO_HIDE_MS: Record<Variant, number> = { success: 4000, warning: 4000, error: 6000 };

type ToastItem = {
  id: number;
  variant: Variant;
  title: string;
  subtitle?: string;
};

// Enriched payload som middleware kan skicka
type EnrichedPatch = RtkqPatchDetail & {
  affectedItems?: number; // t.ex. add + remove
  ops?: { add: number; remove: number; replace: number }; // rena op-räkningar
};

function endpointInfo(patch: RtkqPatchDetail | null, err: RtkqPatchErrorDetail | null) {
  const ep = patch?.endpointName ?? err?.endpointName;
  const args = patch?.originalArgs ?? err?.originalArgs;
  if (!ep) return undefined;
  try {
    const s = typeof args === "string" ? args : JSON.stringify(args);
    return `${ep}(${s.length > 80 ? s.slice(0, 80) + "…" : s})`;
  } catch {
    return ep;
  }
}

export const RtkqPatchToast: React.FC = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    const onPatch = (e: Event) => {
      const d = (e as CustomEvent<EnrichedPatch>).detail;

      // Räkna hur många element som faktiskt påverkades.
      // Om middleware ger affectedItems, använd det. Annars summera ops.
      const affected =
        typeof d?.affectedItems === "number"
          ? d.affectedItems
          : d?.ops
          ? (d.ops.add ?? 0) + (d.ops.replace ?? 0) + (d.ops.remove ?? 0)
          : undefined;

      // Robust fallback: om vi inte kan räkna affected, men det finns patches, så är det ändå en ändring.
      const isChanged = affected != null ? affected > 0 : (d?.patchesCount ?? 0) > 0 || !!d?.changed;

      const variant: Variant = isChanged ? "success" : "warning";
      const title = isChanged
        ? `Patch OK${d?.patchesCount != null ? ` (${d.patchesCount})` : ""}`
        : `Patch uten endring${d?.patchesCount != null ? ` (${d.patchesCount})` : ""}`;

      // Visa endpoint + “berørte elementer” + op-fördelning
      const metrics = [
        affected != null ? `${affected} element${affected === 1 ? "" : "er"} berørt` : null,
        d?.ops ? `+${d.ops.add ?? 0} / ~${d.ops.replace ?? 0} / -${d.ops.remove ?? 0}` : null,
      ]
        .filter(Boolean)
        .join(" • ");

      const subtitleBase = endpointInfo(d, null);
      const subtitle = [subtitleBase, metrics].filter(Boolean).join(" • ");

      const id = Date.now() + Math.random();
      setToasts((prev) => [{ id, variant, title, subtitle }, ...prev]);
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), AUTO_HIDE_MS[variant]);
    };

    const onError = (e: Event) => {
      const detail = (e as CustomEvent<RtkqPatchErrorDetail>).detail;
      const msg =
        (detail?.error as any)?.data?.message ??
        (detail?.error as any)?.error ??
        (detail?.error as any)?.message ??
        "Ukjent feil";
      const id = Date.now() + Math.random();
      setToasts((prev) => [
        { id, variant: "error", title: `Feil ved forespørsel – ${msg}`, subtitle: endpointInfo(null, detail) },
        ...prev,
      ]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, AUTO_HIDE_MS.error);
    };

    window.addEventListener(RTKQ_PATCH_EVENT, onPatch);
    window.addEventListener(RTKQ_PATCH_ERROR_EVENT, onError);
    return () => {
      window.removeEventListener(RTKQ_PATCH_EVENT, onPatch);
      window.removeEventListener(RTKQ_PATCH_ERROR_EVENT, onError);
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <Stack
      style={{
        position: "fixed",
        left: 12,
        bottom: 12,
        zIndex: 10000000,
        // pointerEvents: "none", // blockera inte UI
      }}
      aria-live="polite"
    >
      {toasts.map((t) => {
        const icon =
          t.variant === "success" ? (
            <IconCheck size={20} />
          ) : t.variant === "warning" ? (
            <IconExclamationMark size={20} />
          ) : (
            <IconX size={20} />
          );

        return (
          <Notification
            styles={{
              icon: {
                color: t.variant === "error" ? "red" : t.variant === "warning" ? "orange" : "green",
              },
              root: {
                border: "1px solid lightgray",
              },
            }}
            w="500px"
            icon={icon}
            withCloseButton={true}
            key={t.id}
            title={t.title}
            onClose={() => {
              setToasts((prev) => prev.filter((toast) => toast.id !== t.id));
            }}
          >
            {t.subtitle}
          </Notification>
        );
      })}
    </Stack>
  );
};

export default RtkqPatchToast;
